export const claimListDocumentation = `
## Table Columns

| Column | Width | Filter Type | Permission Required | Hideable | Description |
|--------|-------|-------------|-------------------|----------|-------------|
| Account Custom Tags | 270px | Select (bubbles) | ACCOUNTS_GET (for DS account) | <span class="checkmark">✓</span> | Account-level custom tags displayed as colored bubbles |
| Account Business Name | 220px | Select with dynamic hint | ACCOUNTS_GET (for DS account) | <span class="crossmark">✗</span> | Business name of the account (not hideable) |
| Account Custom Identifier | 200px | Text | ACCOUNTS_GET (for DS account) | <span class="checkmark">✓</span> | Account custom identifier |
| Patient First Name | 150px | Text | - | <span class="checkmark">✓</span> | Patient's first name |
| Patient Last Name | 150px | Text | - | <span class="checkmark">✓</span> | Patient's last name |
| Patient MRN | 120px | Text | - | <span class="checkmark">✓</span> | Medical Record Number |
| Patient Date of Birth | 130px | Date range | - | <span class="checkmark">✓</span> | Patient's date of birth (localized format) |
| Patient Custom ID | 130px | Text | - | <span class="checkmark">✓</span> | Custom patient identifier |
| Assessment/Resource | 200px | Text | - | <span class="checkmark">✓</span> | Entity name linked to the claim |
| Entity Type | 120px | Select | - | <span class="checkmark">✓</span> | Program, Assessment, Resource, IntellyChart |
| Order Date | 110px | Date range | - | <span class="checkmark">✓</span> | Date when order was placed |
| Appointment Date | 130px | Date range | - | <span class="checkmark">✓</span> | Scheduled appointment date |
| Document Date | 120px | Date range | - | <span class="checkmark">✓</span> | Document creation/upload date |
| Claim Generated Date | 150px | Date range | - | <span class="crossmark">✗</span> | When the claim was generated (default sort, not hideable) |
| Billing Status | 120px | Select | - | <span class="checkmark">✓</span> | Current billing status with color-coded display |
| Claim Status | 120px | Select | - | <span class="checkmark">✓</span> | Overall claim processing status |
| Patient Seen | 100px | Yes/No | - | <span class="checkmark">✓</span> | Yes/No indicator |
| Document Uploaded | 140px | Yes/No | - | <span class="checkmark">✓</span> | Yes/No indicator |
| Total Charge | 120px | Number range | - | <span class="checkmark">✓</span> | Sum of all CPT charges (currency format) |
| Total Paid | 120px | Number range | - | <span class="checkmark">✓</span> | Total amount paid (currency format) |
| Total Patient Responsibility | 180px | Number range | - | <span class="checkmark">✓</span> | Patient responsibility amount (currency format) |

## Account Type Dependencies

### Distribution Sponsors
- See additional account-related columns when viewing claims across multiple accounts
- Different table key used: \`claimsDSTable\`
- Enhanced billing status filter options (Billed, ICD Issue, Insurance Issue)
- Can view and edit claims from child accounts
- Permission checks differ for child account claims vs current account

### Regular Accounts
- Table key: \`claimsTable\`
- Limited billing status options (Pending, Hold, Appointment Type Issue, etc.)
- Standard column visibility based on permissions
- Limited to viewing/managing own account claims only

## Row Actions

| Action | Icon | Permission Required | Description |
|--------|------|-------------------|-------------|
| Download Program PDF | Download | SURVEYS_GET | Downloads PDF report for Program entity types. File format: 'Program: [Entity Name] - [Patient Last Name] [Patient First Name] [Order Timestamp].pdf' |
| Navigate to Claim Edit | Click Row | CLAIMS_GET | Opens claim edit page. DS accounts use cross-account route with accountUuid parameter. |

## Header Actions

| Action | Permission Required | Description |
|--------|-------------------|-------------|
| Metrics Button | CLAIMS_GET | Opens info pane with claims aggregations, counts, and financial summaries from API response |
| CSV Export | CLAIMS_GET | Downloads 'Claims [Date].csv' with current filtered data, respects filters and sort order |
| Refresh Button | CLAIMS_GET | Reloads data while preserving current filters and sort settings |

## Filtering and Search

### Text Filters
- **Patient First Name**: Include, exact match, or starts-with filtering
- **Patient Last Name**: Include, exact match, or starts-with filtering
- **Patient MRN**: Include, exact match, or starts-with filtering
- **Account Business Name**: Include, exact match, or starts-with filtering
- **Account Custom Identifier**: Include, exact match, or starts-with filtering
- **Assessment/Resource**: Include, exact match, or starts-with filtering

### Select Filters
- **Account Custom Tags**: Predefined options from \`CUSTOM_TAGS_OPTIONS\`
- **Billing Status**: Status options (varies by account type - see DS vs Regular differences below)
- **Claim Status**: Processing status options
- **Entity Type**: Program, Assessment, Resource, IntellyChart

### Date Filters
- **Order Date**: Date range picker with localized date formatting
- **Appointment Date**: Date range picker with localized date formatting
- **Document Date**: Date range picker with localized date formatting
- **Claim Generated Date**: Date range picker with localized date formatting
- Supports from/to date selection

### Yes/No Filters
- **Patient Seen**: Yes/No/All options
- **Document Uploaded**: Yes/No/All options

### DS vs Regular Filter Differences

#### DS-Exclusive Filter Options
- **Billing Status**: Billed, ICD Issue, Insurance Issue (additional to standard options)
- **Enhanced Account Filtering**: Cross-account tag filtering, child account selection

#### Regular Account Filter Options
- **Billing Status**: Pending, Hold, Appointment Type Issue, Appointment Cancelled, Ready to Bill

### Filter Persistence
- Filters stored in sessionStorage with daily expiration
- Account-specific storage using hashed account UUID
- Maintains filter state across browser sessions

## Table Features

### Virtual Scrolling
- Uses Mantine React Table for performance with large datasets
- Dynamic page size calculation based on viewport
- Infinite scroll with automatic loading of additional pages
- Loading shimmer effects during data fetch

### Column Management
- **Sortable**: All columns support sorting (default: Claim Generated Date descending)
- **Resizable**: Users can adjust column widths
- **Reorderable**: Drag and drop column reordering
- **Pinnable**: Pin important columns to left or right
- **Hideable**: Most columns can be hidden (except Claim Generated Date and Account Business Name)

### Data Display
- Empty values display as "-" or "N/A" as appropriate
- Custom tags shown as colored bubbles with sort relevance
- Dates displayed in localized format
- Financial amounts in proper currency format
- Status columns with color-coded indicators

## Permissions

### Required for Table Access
- **CLAIMS_GET**: Basic permission to view claims list

### Column-Specific Permissions
- **ACCOUNTS_GET**: Required for account-related columns (Account Custom Tags, Account Business Name, Account Custom Identifier) - indicates Distribution Sponsor account type
- **SURVEYS_GET**: Required for PDF download functionality

### Action Permissions
- **CLAIMS_GET**: View and navigate to claim details
- **SURVEYS_GET**: Download Program PDF reports

### Permission Context
For Distribution Sponsors viewing child account claims:
- Permission checks use \`childAccountPermissions\`
- Actions may be restricted based on child account permission set
- Failed permission attempts show warning notifications

## API Endpoints

| Method | Endpoint | Description | Parameters |
|--------|----------|-------------|------------|
| GET | \`/accounts/{accountUuid}/claims\` | Fetch claims list | fields, pagination, filters, sorting |
| GET | \`/accounts/{accountUUID}/surveys/{entityUUID}/program-report/pdf\` | Download Program PDF | entityUUID |
| GET | \`/export-claims.csv\` | Export claims to CSV | current filters and sorting |

### Query Parameters
- **fields**: Comma-separated list of columns to retrieve
- **page**: Page number for pagination
- **limit**: Number of items per page
- **sort**: Sorting specification
- **filter**: Applied filter parameters

## Table Personalization

### Persistent Settings
Settings are stored in Redux and persist across sessions:

- **Column Visibility**: Which columns are shown/hidden
- **Column Order**: Custom arrangement of columns
- **Column Sizing**: User-adjusted column widths
- **Column Pinning**: Pinned column positions
- **Sort Preferences**: Default sorting configuration
- **Filter Preferences**: Recently used filter combinations

### Default Configuration
- **Default Sort**: Claim Generated Date (descending)
- **Default Columns**: All columns visible except those requiring missing permissions
- **Default Page Size**: Dynamic based on viewport height
- **Default Filters**: None applied

## Loading States

### Data Loading
- Shimmer effects during initial load and pagination
- Loading indicators for filter application
- Progress indicators for PDF downloads
- Row-specific loading states during actions

### Error Handling
- Network error notifications
- Permission error warnings
- Data validation error messages
- Retry mechanisms for failed requests
`;
